import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
    Button,
    Checkbox,
    Col,
    Divider,
    Form,
    Input,
    notification,
    Row,
    Select,
} from "antd";
import Modal from "antd/lib/modal/Modal";
import React, { useEffect } from "react";
import HTTP from "../../services/httpService";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const LABEL_COL = { span: 8 };
const WRAPPER_COL = { span: 16 };

const FIELD_STYLE = { marginBottom: 0 };

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionDivider = ({ label }) => (
    <Divider
        orientation="left"
        style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.35)",
            borderColor: "rgba(255,255,255,0.08)",
            margin: "16px 0 12px",
        }}
    >
        {label}
    </Divider>
);

// ─── Component ────────────────────────────────────────────────────────────────

const AddEditAlarm = ({
    record,
    visible,
    onClose,
    hotels,
    alarmPriority,
    alarmType,
}) => {
    const [form] = Form.useForm();
    const isEdit = Boolean(record?.id);

    // Populate form when record changes
    useEffect(() => {
        const {
            alarm_name,
            priority,
            alarm_send_to_email,
            alarm_send_to_phone,
            alarm_email_ids,
            alarm_phone_numbers,
            alarm_hotel_ids,
            alarm_type,
        } = record;
        form.setFieldsValue({
            alarm_name,
            priority,
            alarm_send_to_email,
            alarm_send_to_phone,
            alarm_email_ids,
            alarm_phone_numbers,
            alarm_hotel_ids,
            alarm_type,
        });
    }, [record]);

    // ── API calls ─────────────────────────────────────────────────────────────

    const saveAlarm = async (endpoint, formData) => {
        const { success, message } = await HTTP(endpoint, formData);
        onClose();
        if (success === 0) {
            notification.error({ message });
        } else {
            notification.info({ message });
        }
    };

    const handleSubmit = (values) => {
        const payload = { ...values, staff_id: record.staff_id };
        if (isEdit) {
            saveAlarm("/alarms/editAlarm", { ...payload, id: record.id });
        } else {
            saveAlarm("/alarms/addAlarm", payload);
        }
    };

    // ── Dynamic field lists ───────────────────────────────────────────────────

    const PhoneFields = () => (
        <Form.List name="alarm_phone_numbers">
            {(fields, { add, remove }) => {
                if (!fields.length) add();
                return (
                    <>
                        {fields.map((field, index) => (
                            <Form.Item
                                key={field.key}
                                label={index === 0 ? "Phone Numbers" : ""}
                                style={FIELD_STYLE}
                                labelCol={LABEL_COL}
                                wrapperCol={WRAPPER_COL}
                            >
                                <Row align="middle" gutter={8}>
                                    <Col flex="auto">
                                        <Form.Item {...field} noStyle>
                                            <PhoneInput
                                                country="us"
                                                placeholder="Phone number"
                                                inputStyle={{ width: "100%" }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    {fields.length > 1 && (
                                        <Col flex="none">
                                            <MinusCircleOutlined
                                                className="dynamic-delete-button"
                                                onClick={() =>
                                                    remove(field.name)
                                                }
                                            />
                                        </Col>
                                    )}
                                </Row>
                            </Form.Item>
                        ))}
                        <Form.Item
                            wrapperCol={{ offset: 8, span: 16 }}
                            style={{ marginTop: 8 }}
                        >
                            <Button
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => add()}
                            >
                                Add Phone
                            </Button>
                        </Form.Item>
                    </>
                );
            }}
        </Form.List>
    );

    const EmailFields = () => (
        <Form.List name="alarm_email_ids">
            {(fields, { add, remove }) => {
                if (!fields.length) add();
                return (
                    <>
                        {fields.map((field, index) => (
                            <Form.Item
                                key={field.key}
                                label={index === 0 ? "Emails" : ""}
                                style={FIELD_STYLE}
                                labelCol={LABEL_COL}
                                wrapperCol={WRAPPER_COL}
                                required={index === 0}
                            >
                                <Row align="middle" gutter={8}>
                                    <Col flex="auto">
                                        <Form.Item
                                            {...field}
                                            noStyle
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Email is required.",
                                                },
                                                {
                                                    type: "email",
                                                    message:
                                                        "Enter a valid email.",
                                                },
                                            ]}
                                        >
                                            <Input
                                                type="email"
                                                placeholder="Email address"
                                            />
                                        </Form.Item>
                                    </Col>
                                    {fields.length > 1 && (
                                        <Col flex="none">
                                            <MinusCircleOutlined
                                                className="dynamic-delete-button"
                                                onClick={() =>
                                                    remove(field.name)
                                                }
                                            />
                                        </Col>
                                    )}
                                </Row>
                            </Form.Item>
                        ))}
                        <Form.Item
                            wrapperCol={{ offset: 8, span: 16 }}
                            style={{ marginTop: 8 }}
                        >
                            <Button
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => add()}
                            >
                                Add Email
                            </Button>
                        </Form.Item>
                    </>
                );
            }}
        </Form.List>
    );

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            destroyOnHidden
            title={isEdit ? "Edit Alarm" : "New Alarm"}
            footer={null}
            afterClose={() => form.resetFields()}
            width={560}
        >
            <Form
                form={form}
                labelCol={LABEL_COL}
                wrapperCol={WRAPPER_COL}
                onFinish={handleSubmit}
                style={{ marginTop: 16 }}
            >
                {/* ── Basic info ─────────────────────────────────────────── */}
                <SectionDivider label="Basic Info" />

                <Form.Item
                    name="alarm_name"
                    label="Alarm Name"
                    rules={[{ required: true, message: "Name is required." }]}
                >
                    <Input placeholder="Enter alarm name" />
                </Form.Item>

                <Form.Item
                    name="alarm_hotel_ids"
                    label="Hotels"
                    rules={[
                        {
                            required: true,
                            message: "Select at least one hotel.",
                        },
                    ]}
                >
                    <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        placeholder="Search and select hotels"
                        filterOption={(input, option) =>
                            option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                    >
                        {hotels?.data?.map((hotel) => (
                            <Select.Option
                                key={`${hotel.hotel_name}#${hotel.hotel_id}`}
                                value={hotel.hotel_id}
                            >
                                {`${hotel.hotel_id} · ${hotel.hotel_name} · ${hotel.city}`}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="priority" label="Priority">
                    <Select placeholder="Select priority">
                        {alarmPriority.map((item) => (
                            <Select.Option key={item.value} value={item.value}>
                                {item.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* ── Alarm types ────────────────────────────────────────── */}
                <SectionDivider label="Alarm Types" />

                <Form.Item
                    name="alarm_type"
                    label="Types"
                    rules={[
                        {
                            required: true,
                            message: "Select at least one type.",
                        },
                    ]}
                    wrapperCol={{ span: 16 }}
                >
                    <Checkbox.Group style={{ width: "100%" }}>
                        <Row>
                            {alarmType.map((e) => (
                                <Col
                                    xs={24}
                                    key={e.key}
                                    style={{ marginBottom: 10 }}
                                >
                                    <Checkbox value={e.key}>{e.title}</Checkbox>
                                </Col>
                            ))}
                        </Row>
                    </Checkbox.Group>
                </Form.Item>

                {/* ── Notification channels ──────────────────────────────── */}
                <SectionDivider label="Notification Channels" />

                <Form.Item
                    name="alarm_send_to_email"
                    label="Send to Email"
                    valuePropName="checked"
                >
                    <Checkbox />
                </Form.Item>

                <Form.Item
                    name="alarm_send_to_phone"
                    label="Send to Phone"
                    valuePropName="checked"
                >
                    <Checkbox />
                </Form.Item>

                <PhoneFields />
                <EmailFields />

                {/* ── Submit ─────────────────────────────────────────────── */}
                <Divider
                    style={{
                        borderColor: "rgba(255,255,255,0.08)",
                        marginBottom: 16,
                    }}
                />
                <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        style={{ minWidth: 100 }}
                    >
                        {isEdit ? "Save Changes" : "Create Alarm"}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddEditAlarm;
